import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import dayjs from "dayjs";

/**
 * 🔹 Reusable Excel Report Generator
 * 
 * @param {Object} options - Configuration object
 * @param {String} options.title - Report title (e.g., "ATTENDANCE REPORT")
 * @param {String} [options.fileName="report.xlsx"] - File name for download
 * @param {Object[]} options.data - Array of objects to export
 * @param {String[]} [options.dateRange] - Optional date range [from, to]
 * @param {String} [options.extraInfo] - Any additional line info below title
 * @param {String[]} [options.columns] - Optional custom column order
 */
export const downloadExcelReport = async ({
  title,
  fileName = "report.xlsx",
  data = [],
  dateRange = [],
  extraInfo = "",
  columns = [],
}) => {
  try {
    if (!data?.length) {
      Swal.fire("No Data", "No records available to download", "info");
      return;
    }

    Swal.fire({
      title: "Generating Excel...",
      text: "Please wait while we prepare your report.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    // 🔹 Create worksheet
    const worksheet = XLSX.utils.json_to_sheet([]);

    // 🔹 Title & Info
    const metaInfo = [
      [title.toUpperCase()],
      [extraInfo || ""],
      [
        `Date Range: ${
          dateRange?.length
            ? `${dayjs(dateRange[0]).format("DD-MM-YYYY")} to ${dayjs(
                dateRange[1]
              ).format("DD-MM-YYYY")}`
            : "—"
        }`,
      ],
      [],
    ];

    XLSX.utils.sheet_add_aoa(worksheet, metaInfo, { origin: "A1" });

    // 🔹 Add data (from row 5)
    if (columns?.length) {
      // If custom column order is given
      const orderedData = data.map((item) => {
        const newObj = {};
        columns.forEach((key) => (newObj[key] = item[key] ?? ""));
        return newObj;
      });
      XLSX.utils.sheet_add_json(worksheet, orderedData, { origin: "A5" });
    } else {
      XLSX.utils.sheet_add_json(worksheet, data, { origin: "A5" });
    }

    // 🔹 Auto column widths
    const colWidths = Object.keys(data[0]).map(() => ({ wch: 20 }));
    worksheet["!cols"] = colWidths;

    // 🔹 Workbook setup
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    // 🔹 Write file
    XLSX.writeFile(workbook, fileName);

    Swal.close();
    Swal.fire({
      position: "top-end",
      icon: "success",
      title: `${fileName} downloaded successfully`,
      showConfirmButton: false,
      timer: 1500,
    });
  } catch (error) {
    Swal.close();
    console.error("Excel download error:", error);
    Swal.fire("Error", "Failed to download Excel file", "error");
  }
};
